/**
 * The closed door: what her zone shows when she is not talking.
 *
 * Not an error and not an empty state — a room with someone in it who is
 * not answering. She has already said why (the price text came from the
 * server when she left), and the one thing on offer is whatever she asked
 * for. There is no composer, because a text box she ignores is worse than a
 * door: it invites him to keep typing into nothing.
 *
 * Whether paying actually works was decided when she walked off, so this
 * never promises — it offers, and takes her answer.
 */

import { useEffect, useState } from 'react'
import { GhostReveal } from '../stage/GhostReveal.js'
import { type AwayState, type PriceKind, payPrice } from './away.js'

/** How she frames a wait with no way to shorten it. */
const TIRED_NOTE = 'Šiandien viskas. Apskaita irgi turi darbo valandas.'

/** What the button says, per price. */
const PRICE_ACTION: Readonly<Record<PriceKind, string>> = {
  apology: 'Atsiprašau',
  log_meal: 'Patikrink',
  answer: 'Atsakyti',
}

/**
 * Katė delivers the demand about a quarter of the time.
 *
 * Seeded off the lock itself rather than rolled per render, so the messenger
 * does not change while he is standing there reading it.
 */
function kateDelivers(state: AwayState): boolean {
  let hash = 0
  for (const ch of state.until) hash = (hash * 31 + ch.charCodeAt(0)) | 0
  return Math.abs(hash) % 4 === 0
}

const KATE_FRAMES: readonly string[] = [
  'Katė sėdi tarpduryje ir žiūri į tave. Ji perduoda:',
  'Katė atnešė žinutę. Nemirksėdama:',
]

function remaining(until: string): string {
  const ms = Date.parse(until) - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return ''
  const minutes = Math.ceil(ms / 60000)
  if (minutes < 60) return `dar ${minutes} min.`
  return `dar ${Math.ceil(minutes / 60)} val.`
}

export function DoorClosed({
  state,
  onOpened,
}: {
  state: AwayState
  onOpened: () => void
}): React.JSX.Element {
  const [retort, setRetort] = useState<string | null>(null)
  const [answer, setAnswer] = useState('')
  const [busy, setBusy] = useState(false)
  const [spent, setSpent] = useState(state.priceMet)
  const [left, setLeft] = useState(() => remaining(state.until))

  // The wait is the content here, so it has to actually tick.
  useEffect(() => {
    const id = window.setInterval(() => setLeft(remaining(state.until)), 30_000)
    return () => window.clearInterval(id)
  }, [state.until])

  const price = state.priceKind
  const viaKate = price != null && kateDelivers(state)

  const offer = async () => {
    if (price == null || busy) return
    setBusy(true)
    const result = await payPrice(price, price === 'answer' ? answer : undefined)
    setBusy(false)
    if (result.opened) {
      onOpened()
      return
    }
    setSpent(true)
    setRetort(result.retort)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
      <p className="text-[0.6875rem] text-mute uppercase tracking-[0.14em]">
        {state.kind === 'tired' ? 'Baigta šiandienai' : 'Durys uždarytos'}
        {left ? ` · ${left}` : ''}
      </p>

      {viaKate && (
        <p className="text-dim text-sm">{KATE_FRAMES[Math.abs(state.until.length) % 2]}</p>
      )}

      <p className="text-balance text-ink">
        <GhostReveal>{state.priceText ?? TIRED_NOTE}</GhostReveal>
      </p>

      {retort != null && <p className="text-balance text-dim text-sm">{retort}</p>}

      {price === 'answer' && !spent && (
        <textarea
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Vienas sąžiningas atsakymas."
          className="w-full max-w-sm resize-none rounded-[var(--radius-card)] border border-line bg-raised px-3 py-2 text-ink text-sm"
        />
      )}

      {price != null && !spent && (
        <button
          type="button"
          onClick={() => void offer()}
          disabled={busy || (price === 'answer' && answer.trim().length === 0)}
          className="rounded-full border border-line px-4 py-2 text-[0.6875rem] text-[var(--her)] uppercase tracking-[0.14em] disabled:opacity-40"
        >
          {PRICE_ACTION[price]}
        </button>
      )}
    </div>
  )
}
